import { createMuiTheme } from '@material-ui/core'
import { background, primary, content, secondary, status, hyperLink, colors } from './colors'

export const candidateTheme = createMuiTheme({
	spacing: (factor) => `${0.5 * factor}rem`, // 1 spacing unit = 8px
	palette: {
		background,
		primary,
		secondary,
		content,
		status,
		hyperLink,
		error: {
			main: '#fe7d74',
		},
		text: {
			primary: '#000000',
			secondary: '#7E7E80',
			tertiary: '#03606A',
			disabled: '#AEAEAE',
			blue: '#1C5CDB',
			hint: '#868686',
			muted: colors.text.muted,
		},
		action: {
			active: '#868686',
			selected: 'rgba(0, 0, 0, 0.08)',
			disabled: 'rgba(0, 0, 0, 0.26)',
			disabledBackground: 'rgba(0, 0, 0, 0.12)',
		},
		divider: 'rgba(0, 0, 0, 0.12)',
	},
	colors: {
		background,
		primary,
		secondary,
		content,
		status,
		hyperLink,
		error: {
			main: '#fe7d74',
		},
		text: {
			primary: '#000000',
			secondary: '#7E7E80',
			tertiary: '#03606A',
			disabled: '#AEAEAE',
			blue: '#1C5CDB',
			hint: '#868686',
			muted: colors.text.muted,
		},
		action: {
			active: '#868686',
			selected: 'rgba(0, 0, 0, 0.08)',
			disabled: 'rgba(0, 0, 0, 0.26)',
			disabledBackground: 'rgba(0, 0, 0, 0.12)',
		},
		divider: 'rgba(0, 0, 0, 0.12)',
		light: {
			gray: '#E6E6E6',
		},
	},
	shape: {
		borderRadius: 10,
	},
	borders: ['unset', 'solid 1px #e5e5e5', 'solid 1px rgba(130, 206, 207, 0.1)', 'solid 1px #82CECE'],
	typography: {
		fontFamily: ['Inter'],
		h1: {
			fontSize: '56px',
			lineHeight: '72px',
			fontWeight: 700,
			letterSpacing: '0.2px',
		},
		h2: {
			fontSize: '44px',
			lineHeight: '56px',
			fontWeight: 700,
			letterSpacing: '0.2px',
		},
		h3: {
			fontSize: '32px',
			lineHeight: '40px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},
		h4: {
			fontSize: '26px',
			lineHeight: '40px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},
		h5: {
			fontSize: '20px',
			fontStyle: 'normal',
			fontWeight: 700,
			lineHeight: '32px',
		},

		h6: {
			fontSize: '18px',
			lineHeight: '24px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},

		title1: {
			fontSize: '16px',
			lineHeight: '28px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},

		title2: {
			fontSize: '14px',
			lineHeight: '24px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},

		title3: {
			fontSize: '12px',
			lineHeight: '18px',
			fontWeight: 700,
			letterSpacing: '0.1px',
		},

		subtitle1: {
			fontSize: '16px',
			fontStyle: 'normal',
			lineHeight: '28px',
			fontWeight: 600,
			letterSpacing: '0.1px',
		},

		subtitle2: {
			fontSize: '14px',
			lineHeight: '24px',
			fontWeight: 600,
			letterSpacing: '0.2px',
		},
		subtitle3: {
			fontSize: '12px',
			lineHeight: '18px',
			fontWeight: 500,
			letterSpacing: '0.1px',
		},
		body1: {
			fontSize: '16px',
			lineHeight: '28px',
			fontWeight: 400,
			letterSpacing: '0.3px',
			fontStyle: 'normal',
		},
		body2: {
			fontSize: '14px',
			lineHeight: '24px',
			fontWeight: 400,
			letterSpacing: '0.2px',
		},

		body3: { fontSize: '12px', lineHeight: '18px', fontWeight: 400, letterSpacing: '0.1px' },

		price: {
			fontSize: '18px',
			lineHeight: '24px',
			fontWeight: 500,
			letterSpacing: '0.2px',
			color: '#333335',
		},
		button: {
			fontSize: '15px',
			lineHeight: '20px',
			fontWeight: 500,
			letterSpacing: '0px',
		},
		caption: {
			fontSize: '14px',
			lineHeight: '18px',
			fontWeight: 700,
			letterSpacing: '0.4px',
		},
		ghostButton: {
			fontSize: '16px',
			lineHeight: '18px',
			fontWeight: 400,
			letterSpacing: '0.2px',
		},

		footNote: {
			fontSize: '10px',
			lineHeight: '15px',
			fontWeight: 500,
			letterSpacing: '0.1px',
		},
	},
	overrides: {
		MuiDrawer: {
			paperAnchorDockedLeft: {
				borderRight: '1px solid #F4F5F4',
			},
		},
		MuiListItem: {
			root: {
				color: '#868686',
				fontSize: '14px',
				fontWeight: 800,
				letterSpacing: '0.1px',
				'&.Mui-selected': {
					backgroundColor: 'transparent',
				},
			},
		},
		MuiFormHelperText: {
			root: {
				fontSize: '12px',
				fontWeight: 400,
				lineHeight: '20px',
				'&.Mui-error': {
					color: status.error.medium,
				},
			},
		},
		MuiSlider: {
			thumb: {
				height: '16px',
				width: '16px',
				backgroundColor: primary.main,
			},
			track: {
				height: '4px',
				borderRadius: '100px',
				backgroundColor: content.medium,
			},
			rail: {
				height: '4px',
				borderRadius: '100px',
				backgroundColor: background.element.dark,
				opacity: 1,
			},
		},
		MuiFormLabel: {
			root: {
				color: primary.main,
				whiteSpace: 'nowrap',
				fontWeight: 800,
				fontSize: '14px',
				'&$focused': {
					color: primary.main,
				},
			},
		},
		MuiFormControl: {
			root: {
				width: '100%',
				fontSize: '12px',
				fontStyle: 'normal',
				fontWeight: 600,
				letterSpacing: '0.1px',
			},
		},
		MuiFormControlLabel: {
			label: {
				fontWeight: 800,
				fontSize: '12px',
				lineHeight: '18px',
				letterSpacing: '0.2px',

				'& .MuiSvgIcon-root': {
					color: background.element.dark,
				},
			},

			root: {
				'& .Mui-checked .MuiSvgIcon-root': {
					color: primary.main,
				},

				'& .MuiButtonBase-root .MuiTypography-root': {
					color: content.veryDark,
				},
			},
		},
		MuiInputBase: {
			root: {
				color: '#333335',
				fontWeight: 400,
				fontSize: '14px',
			},
		},
		MuiInputLabel: {
			shrink: {
				transform: 'translate(0, 1.5px) scale(0.86)',
			},
		},
		MuiBackdrop: {
			root: {
				backgroundColor: 'transparent !important',
			},
		},
		MuiDialog: {
			container: {
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
			},
		},
		MuiButtonGroup: {
			groupedOutlinedPrimary: {
				'&:hover': {
					borderColor: '#99E1E1',
				},
			},
		},
		MuiButton: {
			root: {
				borderRadius: '8px',
				backgroundColor: background.app.light,
				color: primary.main,
			},
			contained: {
				backgroundColor: background.app.light,
				color: primary.main,
			},
			outlined: {
				color: primary.main,
			},

			outlinedPrimary: {
				border: `2px solid ${primary.main}`,
				backgroundColor: background.app.light,
				'&:hover': {
					border: `2px solid ${primary.main}`,
				},
			},
			containedPrimary: {
				color: '#F8F8F8',
				backgroundColor: primary.main,
				boxShadow: 'none',
				'&.Mui-disabled': {
					color: content.veryLight,
					backgroundColor: content.medium,
				},
				'&:hover': {
					backgroundColor: primary.main,
					color: content.veryLight,
					boxShadow: 'none',
				},
			},
			containedSizeLarge: {
				padding: '8px !important',
			},
			label: {
				'& .MuiTypography-root': {
					fontSize: '16px',
					fontWeight: 700,
					lineHeight: '28px',
				},
				fontSize: '16px',
				lineHeight: '28px',
				fontWeight: 700,
				padding: '4px 12px',
			},

			sizeLarge: {
				height: '48px',
			},
			sizeSmall: {
				height: '32px',
			},
		},
		MuiTab: {
			root: {
				textTransform: 'unset',
				fontSize: '16px',
				fontStyle: 'normal',
				lineHeight: '28px',
				fontWeight: 700,
				letterSpacing: '0.1px',

				minWidth: 'auto', // Allow tabs to shrink to content width
				padding: '6px 0', // Adjust padding
				'&::after': {
					content: '""',
					position: 'absolute',
					bottom: 0,
					left: 0,
					width: '100%',
					height: 3, // Should match the indicator height
					backgroundColor: 'transparent',
					transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
				},

				'&$selected': {
					'& .MuiTab-wrapper': {
						color: primary.main,
					},
				},
			},
			wrapper: {
				color: content.veryDark,
				opacity: 1,
			},
			textColorInherit: {},
		},
		MuiTabs: {
			root: {
				'& .MuiButtonBase-root': {
					opacity: 1,
				},
			},
			indicator: {
				backgroundColor: primary.main,
			},
			flexContainer: {
				gap: 24,
			},
		},
		MuiDivider: {
			root: {
				backgroundColor: '#99E1E1',
			},
		},
		MuiPickersDay: {
			root: {
				fontWeight: 'normal',
			},
		},
		MuiCheckbox: {
			root: {
				color: content.medium,
				borderRadius: '5px !important',
			},
			colorSecondary: {},
			checked: {},
			colorPrimary: {
				color: primary.main,
				'&$checked': {
					color: primary.main,
				},
			},
		},
		MuiTypography: {
			body3: {
				fontSize: '12px',
				lineHeight: '18px',
				fontWeight: 400,
				letterSpacing: '0.1px',
			},
		},
		MuiBottomNavigation: {
			root: {
				height: 'fit-content',
				gap: '8px',
			},
		},

		MuiBottomNavigationAction: {
			root: {
				padding: '21px 16px',
				opacity: 1,
				borderBottom: `4px solid transparent`,
				'&$selected': {
					color: primary.main,
					borderBottom: `4px solid ${primary.main}`,
					padding: '21px 16px !important',

					'& .MuiBottomNavigationAction-label': {
						color: primary.main,
					},

					'& .MuiSvgIcon-root': {
						color: background.element.dark,
					},
				},
			},
			wrapper: {
				display: 'flex',
				flexDirection: 'row',
				gap: '12px',
				color: content.veryDark,
				'& .MuiSvgIcon-root': {
					fill: 'transparent',
				},
			},
			label: {
				'font-family': 'Inter',
				'font-size': '14px',
				'font-style': 'normal',
				'font-weight': '700',
				'line-height': '24px',
				whiteSpace: 'nowrap',

				'&$selected': {
					color: primary.main,
				},
			},
			iconOnly: {
				'& $wrapper': {
					justifyContent: 'center',
				},
			},
		},
		MuiSvgIcon: {
			root: {
				fill: 'currentColor',
			},
			colorPrimary: {
				color: background.element.dark,
			},
			colorSecondary: {
				fill: secondary.main,
			},
			colorAction: {
				color: background.element.dark,
			},
		},
		MuiSwitch: {
			root: {
				width: 36,
				height: 22,
				padding: 0,
				borderRadius: 12,
			},
			input: {},
			switchBase: {
				padding: 1,

				'&$checked': {
					transform: 'translateX(16px)',
					color: '#ffffff',

					'& + $track': {
						backgroundColor: '#0E3F6C',
						opacity: 1,
						border: 'none',
					},
				},
				'&$focusVisible $thumb': {
					color: '#0E3F6C',
					border: '6px solid #fff',
				},

				checked: {},
				focusVisible: {},
			},
			thumb: {
				width: 16,
				height: 16,
				margin: 2,
				backgroundColor: background.app.light,
				boxShadow: 'none',
			},
			track: {
				borderRadius: 12,
				backgroundColor: content.light,
				opacity: 1,
				// border: `1px solid ${theme.palette.grey[400]}`,
				// transition: theme.transitions.create(['background-color', 'border']),
			},
		},
		MuiRadio: {
			root: {
				borderRadius: '50%',

				'& .MuiSvgIcon-root': {
					color: content.medium,
				},
			},
		},
		MuiLinearProgress: {
			root: {
				height: 8,
				borderRadius: 4,
				backgroundColor: content.light,
			},
			colorPrimary: {
				backgroundColor: content.light,
			},
			barColorPrimary: {},
			bar1Determinate: {
				borderRadius: 4,
			},
			bar1Buffer: {
				backgroundColor: primary.main,
			},
			determinate: {
				backgroundColor: {
					backgroundColor: content.light,
				},
			},
		},
		MuiAutocomplete: {
			tag: {
				backgroundColor: background.element.veryLight,
				border: 'none',
				color: primary.main,
				// Typography subtile 2
				fontSize: '14px',
				lineHeight: '24px',
				fontWeight: 600,

				'& .MuiChip-deleteIcon': {
					color: primary.main,
				},
			},
		},
		MuiChip: {
			root: {
				backgroundColor: background.element.veryLight,
				border: 'none',
				color: primary.main,
				// Typography subtile 2
				fontSize: '14px',
				lineHeight: '24px',
				fontWeight: 600,
			},
			deleteIcon: {
				color: primary.main,
			},
		},
	},
})
